<template>
  <div class="CourseLive_wrap replay-page">
    <div class="CourseLive_header">
      <div class="CourseLive_header__secssion">
        <div class="logo-m">
          <img src="~@/assets/images/LOGO_width.png" />
        </div>
        <div class="logo">
          <img src="~@/assets/images/logo_white.png" />
        </div>
      </div>
      <Headertitle
        :isListPage="false"
        :isPlayPage="true"
        :isexitPage="true"
        :associationScore="null"
        :specialistScore="null"
      />
    </div>

    <main class="replay-main">
      <div v-if="loading" class="replay-state">
        <v-progress-circular indeterminate color="primary" />
        <span>영상을 준비하는 중입니다.</span>
      </div>

      <div v-else-if="errorMessage" class="replay-state">
        <v-icon color="error">mdi-alert-circle-outline</v-icon>
        <span>{{ errorMessage }}</span>
        <v-btn color="primary" outlined @click="fetchPlayback">다시 시도</v-btn>
      </div>

      <template v-else-if="lecture">
        <section class="lecture-summary">
          <p class="session-label">
            Session {{ lecture.session.number }} · {{ lecture.time }}
          </p>
          <h1>{{ lecture.title }}</h1>
          <p class="speakers">{{ lecture.speakers.join(', ') }}</p>
        </section>

        <section class="video-shell" @contextmenu.prevent>
          <video
            v-if="streamUrl"
            ref="video"
            :src="streamUrl"
            controls
            controlslist="nodownload noremoteplayback"
            disablepictureinpicture
            disableremoteplayback
            playsinline
            preload="metadata"
            @dragstart.prevent
            @loadedmetadata="onLoadedMetadata"
            @playing="onPlaying"
            @pause="onPause"
            @waiting="onWaiting"
            @stalled="onWaiting"
            @seeking="onSeeking"
            @seeked="onSeeked"
            @ended="onEnded"
            @error="onVideoError"
          ></video>
        </section>
      </template>
    </main>

    <v-footer class="Dashboard__footer">
      <span>한국지질동맥경화학회 - 교육위원회</span>
    </v-footer>
  </div>
</template>

<script>
import Headertitle from '@/components/app/user/Headertitle.vue';
import VodApi from '@/api/vod/VodApi';

export default {
  components: {
    Headertitle,
  },
  data() {
    return {
      lecture: null,
      streamUrl: '',
      loading: false,
      errorMessage: '',
      startEventSent: false,
      watchStartedAt: null,
      pendingWatchMilliseconds: 0,
      watchRequestInFlight: false,
      inFlightWatchPayload: null,
      heartbeatTimer: null,
      exitSent: false,
    };
  },
  mounted() {
    this.fetchPlayback();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('pagehide', this.onPageHide);
    this.heartbeatTimer = window.setInterval(() => {
      this.flushWatch('heartbeat');
    }, 10000);
  },
  beforeDestroy() {
    window.clearInterval(this.heartbeatTimer);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('pagehide', this.onPageHide);
    this.stopWatchClock();
    this.sendKeepalive('exit');
  },
  methods: {
    async fetchPlayback() {
      const lectureId = Number(this.$route.params.lectureId);
      if (!Number.isInteger(lectureId)) {
        this.errorMessage = '잘못된 강의 경로입니다.';
        return;
      }

      this.loading = true;
      this.errorMessage = '';
      this.startEventSent = false;
      try {
        const response = await VodApi.fetchPlayback(lectureId);
        this.lecture = response.lecture;
        this.streamUrl = response.stream_url;
      } catch (error) {
        this.errorMessage = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : '영상을 준비하지 못했습니다.';
      } finally {
        this.loading = false;
      }
    },
    onLoadedMetadata() {
      if (this.startEventSent || !this.lecture || !this.$refs.video) return;

      this.startEventSent = true;
      this.flushWatch('start', true, 0);
    },
    onPlaying() {
      if (document.hidden) return;
      this.startWatchClock();
    },
    onPause() {
      this.stopWatchClock();
      this.flushWatch('pause');
    },
    onWaiting() {
      this.stopWatchClock();
      this.flushWatch('waiting');
    },
    onSeeking() {
      this.stopWatchClock();
    },
    onSeeked() {
      const video = this.$refs.video;
      if (video && !video.paused && !document.hidden) {
        this.startWatchClock();
      }
    },
    onEnded() {
      this.stopWatchClock();
      this.flushWatch('ended');
    },
    onVideoError() {
      this.stopWatchClock();
      this.errorMessage = '영상을 불러오지 못했습니다. 목록으로 돌아가 다시 시도해 주세요.';
    },
    onVisibilityChange() {
      const video = this.$refs.video;
      if (document.hidden) {
        this.stopWatchClock();
        this.flushWatch('hidden');
      } else if (video && !video.paused) {
        this.startWatchClock();
      }
    },
    onPageHide() {
      this.stopWatchClock();
      this.sendKeepalive('exit');
    },
    startWatchClock() {
      if (this.watchStartedAt !== null) return;
      this.watchStartedAt = performance.now();
    },
    captureWatchTime() {
      if (this.watchStartedAt === null) return;
      const now = performance.now();
      this.pendingWatchMilliseconds += Math.max(0, now - this.watchStartedAt);
      this.watchStartedAt = now;
    },
    stopWatchClock() {
      this.captureWatchTime();
      this.watchStartedAt = null;
    },
    currentPosition(overridePosition = null) {
      if (Number.isFinite(overridePosition)) return Number(overridePosition);
      const video = this.$refs.video;
      if (video && Number.isFinite(video.currentTime)) return Number(video.currentTime.toFixed(3));
      return 0;
    },
    async flushWatch(eventType, force = false, overridePosition = null) {
      if (!this.lecture || this.watchRequestInFlight) return;
      this.captureWatchTime();

      const watchedSeconds = Math.min(30, Math.floor(this.pendingWatchMilliseconds / 1000));
      if (!force && watchedSeconds <= 0) return;

      const payload = this.watchPayload(eventType, watchedSeconds, overridePosition);
      const sentMilliseconds = watchedSeconds * 1000;
      this.pendingWatchMilliseconds = Math.max(
        0,
        this.pendingWatchMilliseconds - sentMilliseconds
      );
      this.watchRequestInFlight = true;
      this.inFlightWatchPayload = payload;
      try {
        await VodApi.saveWatch(payload);
      } catch (error) {
        this.pendingWatchMilliseconds += sentMilliseconds;
        console.error('VOD watch log failed:', error);
      } finally {
        if (
          this.inFlightWatchPayload
          && this.inFlightWatchPayload.event_uuid === payload.event_uuid
        ) {
          this.inFlightWatchPayload = null;
        }
        this.watchRequestInFlight = false;
      }
    },
    watchPayload(eventType, watchedSeconds, overridePosition = null) {
      return {
        lecture_id: this.lecture.id,
        session_token: localStorage.getItem('info'),
        event_uuid: this.createUuid(),
        event_type: eventType,
        watched_seconds: watchedSeconds,
        position_seconds: this.currentPosition(overridePosition),
      };
    },
    sendKeepalive(eventType) {
      if (this.exitSent || !this.lecture) return;
      this.captureWatchTime();

      const token = sessionStorage.getItem('UserAccessToken');
      const sessionToken = localStorage.getItem('info');
      if (!token || !sessionToken) return;

      const payloads = [];
      if (this.inFlightWatchPayload) {
        // 진행 중 요청과 동일한 UUID로 한 번 더 보내면 응답 유실 시에도 저장되고,
        // 두 요청이 모두 도착해도 DB unique key에 의해 한 번만 합산됩니다.
        payloads.push(this.inFlightWatchPayload);
      }

      const watchedSeconds = Math.min(30, Math.floor(this.pendingWatchMilliseconds / 1000));
      if (watchedSeconds > 0) {
        payloads.push(this.watchPayload(eventType, watchedSeconds));
        this.pendingWatchMilliseconds = Math.max(
          0,
          this.pendingWatchMilliseconds - watchedSeconds * 1000
        );
      }
      if (payloads.length === 0) return;

      const apiOrigin = `${window.location.protocol}//${window.location.hostname}:8008`;
      payloads.forEach((payload) => {
        fetch(`${apiOrigin}/api/vod/watch`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {});
      });
      this.exitSent = true;
    },
    createUuid() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
        const random = Math.floor(Math.random() * 16);
        const value = character === 'x' ? random : (random & 0x3) | 0x8;
        return value.toString(16);
      });
    },
  },
};
</script>

<style scoped>
.replay-page {
  min-height: 100vh;
  background: #f4f6fb;
}

.replay-main {
  width: min(1120px, calc(100% - 48px));
  margin: 0 auto;
  padding: 34px 0 88px;
}

.replay-state {
  min-height: 480px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: #687084;
}

.lecture-summary {
  margin-bottom: 20px;
  padding: 24px 28px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 8px 28px rgba(28, 45, 94, 0.07);
}

.session-label {
  margin: 0 0 7px;
  color: #3656c9;
  font-weight: 700;
}

.lecture-summary h1 {
  margin: 0;
  color: #242833;
  font-size: 26px;
  line-height: 1.45;
}

.speakers {
  margin: 8px 0 0;
  color: #657087;
}

.video-shell {
  overflow: hidden;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 16px;
  background: #000;
  box-shadow: 0 12px 34px rgba(16, 24, 40, 0.2);
}

.video-shell video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

.playback-note {
  margin: 14px 4px 0;
  color: #7b8292;
  font-size: 13px;
}

@media (max-width: 720px) {
  .replay-main {
    width: calc(100% - 24px);
    padding-top: 18px;
  }

  .lecture-summary {
    padding: 18px;
  }

  .lecture-summary h1 {
    font-size: 20px;
  }

  .video-shell {
    border-radius: 10px;
  }
}
</style>
